import { useCallback, useMemo } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useValidation } from 'components/modules/Insurance/hooks/useValidation';
import * as CarInstanceActions from 'store/carInstance/actions';
import * as CarInstanceSelectors from 'store/carInstance/selectors';
import * as InsuranceActions from 'store/insurance/actions';
import * as InsuranceSelectors from 'store/insurance/selectors';
import * as InsuranceCalculationActions from 'store/insuranceCalculation';
import * as InsuranceCalculationConstants from 'store/insuranceCalculation/constants';
import * as InsuranceCalculationSelectors from 'store/insuranceCalculation/selectors';

import { VALIDATION_KEYS, vehicleInformationSchema } from '../VehicleInformation.validation';

export const useSubmit = () => {
  const dispatch = useDispatch();

  const carInstance = useSelector(CarInstanceSelectors.selectValues);
  const insurance = useSelector(InsuranceSelectors.selectDataValues);
  const isInCredit = useSelector(
    InsuranceCalculationSelectors.selectField(InsuranceCalculationConstants.FIELDS.IS_IN_CREDIT)
  );
  const selectedBank = useSelector(
    InsuranceCalculationSelectors.selectField(InsuranceCalculationConstants.FIELDS.SELECTED_BANK)
  );

  const values = useMemo(() => {
    return {
      [VALIDATION_KEYS.CAR_INSTANCE]: carInstance,
      [VALIDATION_KEYS.INSURANCE]: insurance,
      [VALIDATION_KEYS.INSURANCE_CALCULATION]: {
        [InsuranceCalculationConstants.FIELDS.IS_IN_CREDIT]: isInCredit,
        [InsuranceCalculationConstants.FIELDS.SELECTED_BANK]: selectedBank,
      },
    };
  }, [carInstance, insurance, isInCredit, selectedBank]);

  const { validate } = useValidation({ schema: vehicleInformationSchema, values });

  const onUpdate = useCallback(() => {
    dispatch(InsuranceActions.updateVehicleInformation());
  }, []);

  const onSubmit = useCallback(async () => {
    const {
      [VALIDATION_KEYS.CAR_INSTANCE]: carInstanceErrors,
      [VALIDATION_KEYS.INSURANCE]: insuranceErrors,
      [VALIDATION_KEYS.INSURANCE_CALCULATION]: insuranceCalculationErrors,
    } = await validate();
    const error = Boolean(carInstanceErrors || insuranceErrors || insuranceCalculationErrors);

    if (carInstanceErrors) {
      dispatch(CarInstanceActions.updateErrors(carInstanceErrors));
    }

    if (insuranceErrors) {
      dispatch(InsuranceActions.updateDataErrors(insuranceErrors));
    }

    if (insuranceCalculationErrors) {
      dispatch(InsuranceCalculationActions.insuranceSetErrors(insuranceCalculationErrors));
    }

    if (!error) {
      onUpdate();
    }
  }, [validate, onUpdate]);

  return { onSubmit };
};
